<template>
  <v-card>
    <v-card-title class="text-h5 grey lighten-3">
      {{ formTitle }}
    </v-card-title>
    <v-card-text class="pt-4">
      <v-tabs
        v-model="activeTab"
        :grow="!cashRegister.store.staffDiscountsEnabled"
        background-color="grey lighten-3"
        centered
        class="tabs"
        fixed-tabs
        icons-and-text
        slider-color="transparent"
      >
        <v-tab
          key="customer"
          href="#tab-customer"
        >
          {{ $t('Customer Discount') }}
          <v-icon>fal fa-users</v-icon>
        </v-tab>
        <v-tab
          v-show="cashRegister.store.staffDiscountsEnabled"
          key="staff"
          href="#tab-staff"
        >
          {{ $t('Staff Discount') }}
          <v-icon>fal fa-user-tag</v-icon>
        </v-tab>

        <v-tabs-items v-model="activeTab">
          <v-tab-item
            key="customer"
            value="tab-customer"
          >
            <v-row wrap>
              <v-col col="12">
                <form-wrapper
                  :validator="$v.form"
                  class="mt-6">
                  <v-form
                    ref="form"
                    @submit.prevent="submit"
                  >
                    <form-group name="chosenDiscountValue">
                      <template v-slot:default="{attrs}">
                        <numeric-keyboard
                          ref="keyboard"
                          :clear-input="clearInput"
                          :keyboard-class="keyboardClass"
                          max-width="100%"
                          @clear="$set(form, 'chosenDiscountValue', null)"
                          @input="$set(form, 'chosenDiscountValue', $event)"
                        >
                          <v-text-field
                            v-comma
                            slot="input"
                            slot-scope="{ setKeyboardInput }"
                            ref="input"
                            v-bind="attrs"
                            v-model="form.chosenDiscountValue"
                            :label="$t('Amount')"
                            autocomplete="off"
                            filled
                            hide-details
                            @input="setKeyboardInput($event)"
                          />
                        </numeric-keyboard>
                      </template>
                    </form-group>
                    <v-radio-group
                      v-model="form.chosenDiscountType"
                      class="radio-type justify-center"
                      hide-details
                      mandatory
                      row
                    >
                      <v-radio
                        v-for="(type, key) in discountTypes"
                        :key="key"
                        :label="type.label"
                        :value="type.type"
                        color="primary"
                      />
                    </v-radio-group>
                    <v-slide-y-transition>
                      <p
                        v-show="errorMessage"
                        class="red--text mb-0 mt-4"
                      >{{ errorMessage }}</p>
                    </v-slide-y-transition>
                  </v-form>
                </form-wrapper>
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item
            key="staff"
            value="tab-staff"
          >
            <v-row wrap>
              <v-col col="12">
                <v-btn
                  :loading="$wait.is('saving discount')"
                  block
                  class="mt-5"
                  depressed
                  x-large
                  @click="addStaffDiscount(entityUuid)"
                >
                  <v-icon
                    class="mr-2"
                    size="18px"
                  >fal fa-user-tag</v-icon>
                  {{ $t('Apply {0}', [$t('Staff Discount')]) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <v-spacer v-show="activeTab === 'tab-staff'" />
      <v-slide-y-transition>
        <v-btn
          color="red"
          text
          @click="removeDiscount(entityUuid)"
        >
          <v-icon
            class="mr-2"
            small
          >fal fa-trash-alt</v-icon>
          {{ $t('No {0}', [$t('Discount')]) }}
        </v-btn>
      </v-slide-y-transition>
      <template v-if="activeTab === 'tab-customer'">
        <v-spacer />
        <v-btn
          :loading="$wait.is('saving discount')"
          class="font-weight-bold"
          color="primary"
          text
          @click="submit"
        >
          {{ $t('Save') }}
        </v-btn>
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
import { decimal, required } from 'vuelidate/lib/validators'
import validationMixin from '@/utils/mixins/validationMixin'
import focusInputMixin from '@/utils/mixins/focusInputMixin'
import NumericKeyboard from '@/components/NumericKeyboard'
import { mapGetters } from 'vuex'

export default {
  name: 'FormsDiscountsBaseSave',

  components: {
    NumericKeyboard
  },

  mixins: [
    validationMixin, focusInputMixin
  ],

  props: {
    formTitle: {
      default: '',
      type: String
    },
    keyboardClass: {
      default: 'keyboard',
      type: String
    },
    clearInput: {
      default: false,
      type: Boolean
    }
  },

  validations: {
    form: {
      chosenDiscountType: { required },
      chosenDiscountValue: {
        decimal
      }
    }
  },

  data () {
    return {
      activeTab: 'tab-customer',
      discountTypes: [
        { type: 'fixed', label: 'Euro' },
        { type: 'percentage', label: this.$t('Percent') }
      ],
      discountForStaff: null,
      form: {
        chosenDiscountType: 'fixed',
        chosenDiscountValue: null
      },
      errorMessage: ''
    }
  },

  computed: {
    ...mapGetters(['store', 'cashRegister'])
  },

  watch: {
    'activeTab': function (activeTab) {
      if (activeTab === 'tab-customer') {
        this.resetValues()
        setTimeout(() => { this.$nextTick(this.$refs.input.focus) }, 300)
      }
    },
    'discountForStaff': function (discountForStaff) {
      this.activeTab = discountForStaff ? 'tab-staff' : 'tab-customer'
    },
    'form.chosenDiscountType': function (chosenType) {
      if (!chosenType) {
        this.form.chosenDiscountType = 'fixed'
      }
    }
  },

  mounted () {
    setTimeout(() => { this.$nextTick(this.$refs.input.focus) }, 300)
    // Put error messages in separate reactive data property
    this.$watch(
      () => this.$refs['input'].errorMessages,
      (messages) => {
        this.errorMessage = messages.length ? messages[0] : ''
      }
    )
  },

  methods: {
    addStaffDiscount () {
      // gets extended by child
    },
    resetValues () {
      this.$set(this.form, 'chosenDiscountType', 'fixed')
      this.$set(this.form, 'chosenDiscountValue', null)
    },
    submit () {
      // gets extended by child
    }
  }
}
</script>

<style language="scss" scoped>
.tabs /deep/ .v-tab--active,
.tabs /deep/ .v-tab--active .v-icon {
  background: #f57c00;
  color: white;
}
</style>



// WEBPACK FOOTER //
// src/components/forms/discounts/FormsDiscountsBaseSave.vue